import { ReactElement, useMemo, useState } from "react"
import { useHotspotLegendItems } from "./useHotspotLegendItems"
import { useKMLLegendItems } from "./useKMLLegendItems"
import { useIconLegendItems } from "./useIconLegendItems"
import { useGeometryLegendItems } from "./useGeometryLegendItems"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { selectDisabledItems, selectEnabled, toggleDisabledItem } from "plugins/PDFv2/PDFSlice"
import { useTextLegendItems } from "./useTextLegendItems"

export type LegendItem = {
    icon: ReactElement
    title: string
    id: string
    toggled?: boolean
    hotspotType?: "point" | "heat" | "hex"
    stat?: { label: string; stat: string; isSetByDefault?: boolean }
    secondaryStat?: { label: string; stat: string }
}

export type LegendItemProps = {
    offsetX: number
    offsetY: number
    width: number
    height: number
}

export const useLegendItems = (props: LegendItemProps) => {
    const dispatch = useAppDispatch()
    const disabledItems = useAppSelector(selectDisabledItems())
    const [hasExceededMax, setHasExceededMax] = useState(false)

    const enabled = useAppSelector(selectEnabled())

    const hotspotLegendItems = useHotspotLegendItems(props)
    const kmlLegendItems = useKMLLegendItems(props)
    const iconLegendItems = useIconLegendItems(props)
    const geometryLegendItems = useGeometryLegendItems(props)
    const textLegendItems = useTextLegendItems(props)
    const allLegendItems = useMemo(() => {
        return [
            ...hotspotLegendItems
                .map((e) => ({ ...e, toggled: !disabledItems[e.id] }))
                .filter((e) => e.hotspotType !== "hex"),
            ...kmlLegendItems.map((e) => ({ ...e, toggled: !disabledItems[e.id] })),
            ...iconLegendItems.map((e) => ({ ...e, toggled: !disabledItems[e.id] })),
            ...geometryLegendItems.map((e) => ({ ...e, toggled: !disabledItems[e.id] })),
            ...textLegendItems.map((e) => ({ ...e, toggled: !disabledItems[e.id] })),
        ]
    }, [
        hotspotLegendItems,
        kmlLegendItems,
        iconLegendItems,
        geometryLegendItems,
        textLegendItems,
        disabledItems,
    ])

    const maxLegendItems = useMemo(() => {
        let baseMax = 40
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { text1, inset, compass, borderticks, gridlines, ...items } = enabled
        const enabledCount = Object.values(items).filter((item) => !!item).length
        if (text1) baseMax -= 7
        if (inset) baseMax -= 7
        return baseMax - enabledCount
    }, [enabled])

    const { enabledLegendItems, excludedLegendItems } = useMemo(() => {
        const enabled = []
        const leftover = []

        for (const item of allLegendItems) {
            if (disabledItems[item.id]) {
                leftover.push(item)
            } else if (enabled.length < maxLegendItems) {
                enabled.push(item)
            } else {
                leftover.push(item)
            }
        }

        return {
            enabledLegendItems: enabled,
            excludedLegendItems: leftover,
        }
    }, [allLegendItems, disabledItems, maxLegendItems])

    const toggle = (id: string) => {
        if (disabledItems[id] && enabledLegendItems.length >= maxLegendItems) {
            setHasExceededMax(true)
            setTimeout(() => setHasExceededMax(false), 4000)
            return
        }

        dispatch(toggleDisabledItem(id))
    }
    return {
        allLegendItems,
        enabledLegendItems,
        excludedLegendItems,
        toggle,
        maxLegendItems,
        hasExceededMax,
    }
}
